import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, FUNCTIONS_URL } from '../lib/supabase';

const AppContext = createContext(null);

// ── Data shapers (DB → UI shape) ──────────────────────────────────────────────

function shapeProgrammes(rows) {
  return (rows || [])
    .sort((a, b) => a.display_order - b.display_order)
    .map(p => ({
      id: p.id, name: p.name, description: p.description, icon: p.icon, color: p.color,
      courses: (p.courses || [])
        .sort((a, b) => a.display_order - b.display_order)
        .map(c => ({
          id: c.id, name: c.name, description: c.description,
          category: c.category, link: c.link,
          studentCount: c.student_count, placeholder: c.placeholder,
        })),
    }));
}

function shapeStudents(rows) {
  return (rows || []).map(s => ({
    id: s.id, name: s.name, email: s.email, role: s.role,
    programmes: (s.enrolments || []).map(e => e.programme_id),
  }));
}

function shapeAdminEmails(rows) {
  return (rows || []).map(e => {
    const readMap = {};
    (e.email_recipients || []).forEach(r => { readMap[r.student_id] = r.read; });
    return {
      id: e.id,
      from: e.from_id,
      fromName: e.from_name,
      fromEmail: e.from_email || '',
      to: (e.email_recipients || []).map(r => r.student_id),
      toAll: e.to_all,
      toProgramme: e.to_programme,
      subject: e.subject,
      body: e.body,
      date: e.created_at,
      read: readMap,
    };
  });
}

function shapeStudentEmails(rows, userId) {
  return (rows || []).map(r => ({
    id: r.emails.id,
    from: r.emails.from_id,
    fromName: r.emails.from_name,
    fromEmail: r.emails.from_email || '',
    to: [userId],
    toAll: r.emails.to_all,
    toProgramme: r.emails.to_programme,
    subject: r.emails.subject,
    body: r.emails.body,
    date: r.emails.created_at,
    read: { [userId]: r.read },
  }));
}

function shapeAuditLog(rows) {
  return (rows || []).map(e => ({
    id: e.id,
    timestamp: e.created_at,
    actor: e.actor_name,
    action: e.action,
    details: e.details,
  }));
}

// ── Provider ──────────────────────────────────────────────────────────────────

const EMPTY = { programmes: [], students: [], emails: [], auditLog: [] };

export function AppProvider({ children }) {
  const [data, setData] = useState(EMPTY);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recoveryMode, setRecoveryMode] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) initUser(session);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryMode(true);
        setLoading(false);
        return;
      }
      if (event === 'SIGNED_OUT' || !session) {
        setUser(null);
        setData(EMPTY);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const initUser = async (session) => {
    try {
      const [{ data: profile }, { data: enrolments }] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', session.user.id).single(),
        supabase.from('enrolments').select('programme_id').eq('student_id', session.user.id),
      ]);
      const currentUser = { ...profile, email: session.user.email, programmes: (enrolments || []).map(e => e.programme_id) };
      setUser(currentUser);
      await loadAppData(currentUser);
    } catch (e) {
      console.error('initUser error', e);
    } finally {
      setLoading(false);
    }
  };

  const loadAppData = async (currentUser) => {
    const isAdmin = currentUser.role === 'admin';

    const [progsRes, emailsRes, auditRes] = await Promise.all([
      supabase.from('programmes').select('*, courses(*)').order('display_order'),
      isAdmin
        ? supabase.from('emails').select('*, email_recipients(student_id, read)').order('created_at', { ascending: false })
        : supabase.from('email_recipients')
            .select('read, emails(id, from_id, from_name, from_email, subject, body, to_all, to_programme, created_at)')
            .eq('student_id', currentUser.id),
      isAdmin
        ? supabase.from('audit_log').select('*').order('created_at', { ascending: false })
        : Promise.resolve({ data: [] }),
    ]);

    let students = [];
    if (isAdmin) {
      const { data: rows } = await supabase.rpc('get_students_with_email');
      students = rows || [];
    }

    setData({
      programmes: shapeProgrammes(progsRes.data),
      students,
      emails: isAdmin ? shapeAdminEmails(emailsRes.data) : shapeStudentEmails(emailsRes.data, currentUser.id),
      auditLog: shapeAuditLog(auditRes.data),
    });
  };

  // ── Auth ──────────────────────────────────────────────────────────────────
  const login = async (email, password) => {
    const { data: { session }, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await initUser(session);
  };

  const signUp = async (name, email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { name: name.trim() } },
    });
    if (error) throw error;
    if (data.session) {
      await initUser(data.session);
      return { needsEmailConfirmation: false };
    }
    return { needsEmailConfirmation: true };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setData(EMPTY);
  };

  const requestPasswordReset = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    const { data: { session } } = await supabase.auth.getSession();
    setRecoveryMode(false);
    await initUser(session);
  };

  // ── Programmes ────────────────────────────────────────────────────────────
  const createProgramme = async (prog) => {
    const id = prog.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + Date.now();
    const { data: row } = await supabase.from('programmes').insert({
      id, name: prog.name.trim(), description: prog.description?.trim() || '',
      icon: prog.icon, color: prog.color, display_order: data.programmes.length,
    }).select().single();
    if (row) setData(d => ({ ...d, programmes: [...d.programmes, { ...row, courses: [] }] }));
  };

  const deleteProgramme = async (id) => {
    await supabase.from('programmes').delete().eq('id', id);
    setData(d => ({ ...d, programmes: d.programmes.filter(p => p.id !== id) }));
  };

  // ── Courses ───────────────────────────────────────────────────────────────
  const addCourse = async (programmeId, form) => {
    const prog = data.programmes.find(p => p.id === programmeId);
    const { data: row } = await supabase.from('courses').insert({
      id: 'c' + Date.now(), programme_id: programmeId,
      name: form.name, description: form.description || '',
      category: form.category || '', link: form.link || null,
      student_count: form.studentCount ? parseInt(form.studentCount) : null,
      placeholder: !form.link, display_order: prog?.courses.length || 0,
    }).select().single();
    if (row) {
      const course = { id: row.id, name: row.name, description: row.description, category: row.category, link: row.link, studentCount: row.student_count, placeholder: row.placeholder };
      setData(d => ({ ...d, programmes: d.programmes.map(p => p.id === programmeId ? { ...p, courses: [...p.courses, course] } : p) }));
    }
  };

  const updateCourse = async (programmeId, courseId, form) => {
    const studentCount = form.studentCount ? parseInt(form.studentCount) : null;
    await supabase.from('courses').update({
      name: form.name, description: form.description || '',
      category: form.category || '', link: form.link || null,
      student_count: studentCount, placeholder: !form.link,
    }).eq('id', courseId);
    setData(d => ({ ...d, programmes: d.programmes.map(p => p.id === programmeId ? { ...p, courses: p.courses.map(c => c.id === courseId ? { ...c, name: form.name, description: form.description, category: form.category, link: form.link, studentCount, placeholder: !form.link } : c) } : p) }));
  };

  const deleteCourse = async (programmeId, courseId) => {
    await supabase.from('courses').delete().eq('id', courseId);
    setData(d => ({ ...d, programmes: d.programmes.map(p => p.id === programmeId ? { ...p, courses: p.courses.filter(c => c.id !== courseId) } : p) }));
  };

  // ── Students ──────────────────────────────────────────────────────────────
  const callFn = async (name, body) => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${FUNCTIONS_URL}/${name}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || `${name} failed`);
    return json;
  };

  const createStudent = async ({ name, email, password, programmes: progs }) => {
    const result = await callFn('create-student', { name, email, password, programmes: progs });
    setData(d => ({ ...d, students: [...d.students, result.user] }));
  };

  const updateStudent = async (id, { name, email, password, programmes: progs }) => {
    const existing = data.students.find(s => s.id === id);
    await supabase.from('profiles').update({ name }).eq('id', id);
    await supabase.from('enrolments').delete().eq('student_id', id);
    if (progs?.length > 0) {
      await supabase.from('enrolments').insert(progs.map(pid => ({ student_id: id, programme_id: pid })));
    }
    if (email !== existing?.email || password) {
      await callFn('update-student', { studentId: id, email: email !== existing?.email ? email : undefined, password: password || undefined });
    }
    setData(d => ({ ...d, students: d.students.map(s => s.id === id ? { ...s, name, email, programmes: progs } : s) }));
  };

  const removeStudent = async (id) => {
    await callFn('delete-student', { studentId: id });
    setData(d => ({ ...d, students: d.students.filter(s => s.id !== id) }));
  };

  // ── Email ─────────────────────────────────────────────────────────────────
  const sendEmail = async ({ toAll, toProgramme, toStudents, subject, body: msgBody }) => {
    const { data: row } = await supabase.from('emails').insert({
      from_id: user.id, from_name: user.name, from_email: user.email,
      subject, body: msgBody, to_all: toAll, to_programme: toProgramme || null,
    }).select().single();
    if (!row) return;

    let recipients = toStudents || [];
    if (toAll) recipients = data.students.map(s => s.id);
    else if (toProgramme) recipients = data.students.filter(s => s.programmes.includes(toProgramme)).map(s => s.id);

    if (recipients.length > 0) {
      await supabase.from('email_recipients').insert(recipients.map(sid => ({ email_id: row.id, student_id: sid, read: false })));
    }

    const readMap = Object.fromEntries(recipients.map(sid => [sid, false]));
    setData(d => ({ ...d, emails: [{ id: row.id, from: row.from_id, fromName: row.from_name, fromEmail: row.from_email, to: recipients, toAll: row.to_all, toProgramme: row.to_programme, subject: row.subject, body: row.body, date: row.created_at, read: readMap }, ...d.emails] }));
  };

  const markEmailRead = async (emailId) => {
    await supabase.from('email_recipients').update({ read: true }).eq('email_id', emailId).eq('student_id', user.id);
    setData(d => ({ ...d, emails: d.emails.map(e => e.id === emailId ? { ...e, read: { ...e.read, [user.id]: true } } : e) }));
  };

  // ── Audit log ─────────────────────────────────────────────────────────────
  const addAuditLog = async (action, details) => {
    const { data: row } = await supabase.from('audit_log').insert({ actor_name: user?.name || 'Admin', action, details }).select().single();
    if (row) setData(d => ({ ...d, auditLog: [{ id: row.id, timestamp: row.created_at, actor: row.actor_name, action: row.action, details: row.details }, ...d.auditLog] }));
  };

  return (
    <AppContext.Provider value={{
      data, user, loading, recoveryMode,
      login, signUp, logout, requestPasswordReset, updatePassword,
      createProgramme, deleteProgramme,
      addCourse, updateCourse, deleteCourse,
      createStudent, updateStudent, removeStudent,
      sendEmail, markEmailRead,
      addAuditLog,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
